// Storefront progress is saved in this browser for the 2026 campaign.
export const GAME_STORAGE_KEY = 'hoto-day-game:hoto-day-2026:v1';
export const GLYPHS = {
  H: ['10001', '10001', '10001', '11111', '10001', '10001', '10001'],
  O: ['01110', '10001', '10001', '10001', '10001', '10001', '01110'],
  T: ['11111', '00100', '00100', '00100', '00100', '00100', '00100'],
};
export const CELLS = (() => {
  let id = 0;
  return Array.from({ length: 7 }, (_, row) => ['H', 'O', 'T', 'O'].map(letter => GLYPHS[letter][row]).join('0')
    .split('').map((bit, col) => ({ row, col, id: bit === '1' ? id++ : null }))).flat();
})();
export const SCREW_COUNT = CELLS.filter(cell => cell.id !== null).length;
export function outcomeForRoll(roll) {
  if (!Number.isFinite(roll) || roll < 0 || roll >= 1) throw new Error('Invalid random roll');
  return roll < 0.4 ? 0 : roll < 0.8 ? 10 : 15;
}
export function initialState() {
  return { removed: [], reward: null };
}
export function validateState(state) {
  if (!state || !Array.isArray(state.removed)
    || state.removed.length > SCREW_COUNT || new Set(state.removed).size !== state.removed.length
    || state.removed.some(id => !Number.isInteger(id) || id < 0 || id >= SCREW_COUNT)
    || !(state.reward === null || (state.removed.length > 0 && [10, 15].includes(state.reward?.discount)
      && typeof state.reward.code === 'string' && state.reward.code.trim().length > 0 && state.reward.code.length <= 128))) {
    throw new Error('Invalid game state');
  }
  return { removed: [...state.removed], reward: state.reward ? { discount: state.reward.discount, code: state.reward.code.trim() } : null };
}
export function playScrew(state, id, roll, codes) {
  validateState(state);
  if (!Number.isInteger(id) || id < 0 || id >= SCREW_COUNT) throw new Error('Invalid screw');
  if (state.reward || state.removed.includes(id)) return state;
  const discount = outcomeForRoll(roll);
  const next = { ...state, removed: [...state.removed, id], reward: discount ? { discount, code: codes[discount] } : null };
  return validateState(next);
}

export function campaignCodes(code10, code15, preview = false) {
  const codes = { 10: String(code10 || '').trim(), 15: String(code15 || '').trim() };
  for (const discount of [10, 15]) {
    if (!codes[discount] && preview) codes[discount] = `PREVIEW${discount}`;
    if (!codes[discount] || codes[discount].length > 128) throw new Error('Missing discount code');
  }
  return codes;
}
export function storedGameState(storage, codes) {
  const saved = storage.getItem(GAME_STORAGE_KEY);
  if (saved === null) return initialState();
  const state = validateState(JSON.parse(saved));
  // A merchant can change a shared code without changing the awarded discount.
  if (state.reward) state.reward.code = codes[state.reward.discount];
  return state;
}
function driveMarkup(id) {
  const polygon = (sides, radius, offset = -90) => Array.from({ length: sides }, (_, i) => {
    const a = (offset + i * 360 / sides) * Math.PI / 180;
    return `${50 + Math.cos(a) * radius},${50 + Math.sin(a) * radius}`;
  }).join(' ');
  const star = spikes => Array.from({ length: spikes * 2 }, (_, i) => {
    const a = (-90 + i * 180 / spikes) * Math.PI / 180, radius = i % 2 ? 18 : 33;
    return `${50 + Math.cos(a) * radius},${50 + Math.sin(a) * radius}`;
  }).join(' ');
  const cross = '<path d="M42 18h16v24h24v16H58v24H42V58H18V42h24z"/>';
  const shapes = [cross, '<rect x="16" y="43" width="68" height="14" rx="3"/>',
    `${cross}<path d="M28 28l44 44m0-44L28 72" stroke="currentColor" stroke-width="5"/>`,
    `${cross}<rect x="10" y="45" width="80" height="10"/>`,
    `<polygon points="${polygon(6, 31, 0)}"/>`, `<polygon points="${star(6)}"/>`,
    `<polygon points="${star(6)}"/><circle cx="50" cy="50" r="7" fill="#aeb2b7"/>`,
    `<polygon points="${polygon(4, 31, -45)}"/>`, `<polygon points="${polygon(3, 35)}"/>`,
    `<polygon points="${star(5)}"/>`, '<path d="M50 18v32l28 16M50 50L22 66" fill="none" stroke="currentColor" stroke-width="12"/>',
    '<circle cx="31" cy="50" r="11"/><circle cx="69" cy="50" r="11"/>'];
  return `<svg viewBox="0 0 100 100" aria-hidden="true" focusable="false">${shapes[((id * 2654435761) >>> 0) % shapes.length]}</svg>`;
}

if (globalThis.customElements && !customElements.get('hoto-day-game')) {
  customElements.define('hoto-day-game', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.preview = this.dataset.preview === 'true';
      this.status = this.querySelector('[data-status]');
      this.board = this.querySelector('[data-board]');
      this.ticket = this.querySelector('[data-reward]');
      this.ticket.classList.remove('is-pinned');
      this.pinned = false;
      this.retry = this.querySelector('[data-retry]');
      this.state = initialState();
      this.busy = true;
      this.board.innerHTML = CELLS.map(({ id, row, col }) => `<span class="hoto-day__cell${id === null ? ' hoto-day__cell--empty' : ''}" style="--row:${row + 1};--col:${col + 1};--mobile-col:${7 - row}">${id === null ? '' : `<button type="button" class="hoto-day__screw" data-screw="${id}" aria-label="Unscrew screw ${id + 1}" tabindex="-1" disabled><span class="hoto-day__head">${driveMarkup(id)}</span></button>`}</span>`).join('');
      this.buttons = [...this.querySelectorAll('[data-screw]')];
      const options = { signal: this.controller.signal };
      this.addEventListener('click', event => {
        const screw = event.target.closest('[data-screw]');
        if (screw && this.contains(screw)) this.unscrew(Number(screw.dataset.screw));
        if (event.target.closest('[data-copy]')) this.copyCode();
        if (event.target.closest('[data-retry]')) this.prepareGame();
        if (event.target.closest('[data-close]')) this.closeReward();
      }, options);
      this.ticket.addEventListener('close', () => {
        this.copyPending = null;
        this.resetCopyButton();
        this.unlockScroll();
        if (this.isConnected && this.state.reward && !this.pinned) {
          this.pinned = true;
          this.ticket.classList.add('is-pinned');
          this.ticket.show();
          this.querySelector('[data-copy]').focus({ preventScroll: true });
          if (matchMedia('(max-width: 749px)').matches) {
            const card = this.querySelector('.hoto-day__ticket');
            const { top, bottom } = card.getBoundingClientRect();
            if (top < 0 || bottom > window.innerHeight) card.scrollIntoView({ block: 'center', inline: 'nearest' });
          }
        }
      }, options);
      this.ticket.addEventListener('cancel', event => {
        if (this.pinned) event.preventDefault();
      }, options);
      this.board.addEventListener('keydown', event => this.navigate(event), options);
      this.prepareGame();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.copyPending = null;
      if (this.ticket?.open) this.ticket.close();
      this.unlockScroll();
      clearTimeout(this.animationTimer);
      clearTimeout(this.copyTimer);
    }

    prepareGame() {
      if (!this.controller) return;
      this.busy = true;
      this.retry.hidden = true;
      try {
        this.codes = campaignCodes(this.dataset.code10, this.dataset.code15, this.preview);
      } catch {
        this.status.textContent = this.dataset.unavailableText;
        this.render();
        return;
      }
      try {
        if (!this.preview) this.state = storedGameState(globalThis.localStorage, this.codes);
      } catch {
        this.showError();
        return;
      }
      this.busy = false;
      this.announce();
      this.render();
      if (!this.preview) this.pinSavedReward();
    }

    pinSavedReward() {
      if (!this.state.reward || this.ticket.open || !this.isConnected) return;
      this.pinned = true;
      this.ticket.classList.add('is-pinned');
      this.ticket.show();
    }

    unscrew(id) {
      if (this.busy || this.state.reward || this.state.removed.includes(id)) return;
      const lifecycle = this.controller;
      this.busy = true;
      this.render();
      this.status.textContent = this.dataset.loadingText;
      try {
        if (!this.preview) {
          const saved = storedGameState(globalThis.localStorage, this.codes);
          if (JSON.stringify(saved) !== JSON.stringify(this.state)) {
            this.state = saved;
            this.busy = false;
            this.render();
            this.announce();
            this.pinSavedReward();
            return;
          }
        }
        const random = new Uint32Array(1);
        crypto.getRandomValues(random);
        const next = playScrew(this.state, id, random[0] / 4294967296, this.codes);
        if (!this.preview) globalThis.localStorage.setItem(GAME_STORAGE_KEY, JSON.stringify(next));
        const wonThisDraw = Boolean(next.reward);
        if (this.controller !== lifecycle) return;
        this.state = next;
        this.buttons[id].classList.toggle('is-golden', Boolean(this.state.reward));
        this.buttons[id].classList.add('is-unscrewing');
        this.animationTimer = setTimeout(() => {
          if (this.controller !== lifecycle) return;
          this.busy = false;
          this.render();
          this.announce();
          if (wonThisDraw) this.openReward();
          else this.buttons.find(button => !button.disabled)?.focus({ preventScroll: true });
        }, matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 650);
      } catch (error) {
        if (this.controller === lifecycle) this.showError();
      }
    }

    render() {
      if (!this.buttons) return;
      let first = true;
      this.buttons.forEach(button => {
        const removed = this.state.removed.includes(Number(button.dataset.screw));
        button.classList.toggle('is-removed', removed);
        button.disabled = this.busy || removed || Boolean(this.state.reward);
        button.tabIndex = !button.disabled && first ? 0 : -1;
        if (!button.disabled) first = false;
      });
      this.board.setAttribute('aria-busy', String(this.busy));
      this.classList.toggle('has-reward', Boolean(this.state.reward));
      this.ticket.dataset.rewardTier = this.state.reward ? String(this.state.reward.discount) : '';
      if (this.state.reward) {
        this.querySelector('[data-discount]').textContent = `${this.state.reward.discount}%`;
        this.resetCopyButton();
      }
    }

    openReward() {
      if (!this.state.reward || this.ticket.open || !this.isConnected) return;
      this.ticket.showModal();
      const style = document.documentElement.style;
      this.previousOverflow = { value: style.getPropertyValue('overflow'), priority: style.getPropertyPriority('overflow') };
      style.setProperty('overflow', 'hidden');
      this.querySelector('[data-copy]').focus({ preventScroll: true });
    }

    unlockScroll() {
      if (!this.previousOverflow) return;
      const style = document.documentElement.style;
      if (this.previousOverflow.value) style.setProperty('overflow', this.previousOverflow.value, this.previousOverflow.priority);
      else style.removeProperty('overflow');
      this.previousOverflow = null;
    }

    closeReward() {
      if (!this.pinned && this.ticket.open) this.ticket.close();
    }

    announce() {
      this.status.textContent = !this.state.reward && this.state.removed.length === SCREW_COUNT
        ? this.dataset.exhaustedText : '';
    }

    showError() {
      this.busy = true;
      this.render();
      this.status.textContent = this.dataset.errorText;
      this.retry.hidden = false;
    }

    navigate(event) {
      const target = event.target.closest('[data-screw]');
      if (!target || !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      const available = CELLS.filter(cell => cell.id !== null && !this.buttons[cell.id].disabled);
      if (!available.length) return;
      const cell = available.find(item => item.id === Number(target.dataset.screw));
      const mobile = matchMedia('(max-width: 749px)').matches;
      const coords = item => mobile ? [6 - item.row, item.col] : [item.col, item.row];
      const [x, y] = coords(cell);
      const vector = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] }[event.key];
      let next;
      if (!vector) next = event.key === 'Home' ? available[0] : available.at(-1);
      else next = available.map(item => {
        const [nx, ny] = coords(item), dx = nx - x, dy = ny - y;
        return { item, forward: dx * vector[0] + dy * vector[1], cross: Math.abs(dx * vector[1] - dy * vector[0]) };
      }).filter(item => item.forward > 0).sort((a, b) => (a.cross * 100 + a.forward) - (b.cross * 100 + b.forward))[0]?.item;
      if (next) {
        target.tabIndex = -1;
        this.buttons[next.id].tabIndex = 0;
        this.buttons[next.id].focus();
      }
    }

    resetCopyButton() {
      clearTimeout(this.copyTimer);
      this.querySelector('[data-copy-status]').textContent = '';
      if (!this.state.reward) return;
      const button = this.querySelector('[data-copy]');
      button.textContent = `CODE:${this.state.reward.code}`;
      button.setAttribute('aria-label', `CODE:${this.state.reward.code} — ${this.dataset.copyText}`);
    }

    async copyCode() {
      if (!this.state.reward || !this.ticket.open || this.copyPending) return;
      const lifecycle = this.controller;
      const pendingCopy = {};
      const code = this.state.reward.code;
      const button = this.querySelector('[data-copy]');
      this.copyPending = pendingCopy;
      this.resetCopyButton();
      const active = () => this.controller === lifecycle && this.isConnected && this.ticket.open && this.copyPending === pendingCopy;
      try {
        await navigator.clipboard.writeText(code);
        if (!active()) return;
        button.textContent = this.dataset.copiedText;
        button.setAttribute('aria-label', `${code} — ${this.dataset.copiedText}`);
        this.querySelector('[data-copy-status]').textContent = this.dataset.copiedText;
        this.copyTimer = setTimeout(() => this.resetCopyButton(), 2500);
      } catch {
        if (active()) window.prompt(this.dataset.copyErrorText, code);
      } finally {
        if (this.copyPending === pendingCopy) this.copyPending = null;
      }
    }
  });
}
